import { Clock, Trash2 } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import { DatePickerInput } from "@/shared/components/ui/date-picker-input";
import { DynamicSelectFilter } from "@/shared/components/filters/Filters";
import type { Airport, SystemDictionary } from "@/shared/api/filters";
import type { FlightFormState } from "./CreateOrderModal";

interface FlightInfoFormProps {
  state: FlightFormState;
  errors: Record<string, string>;
  onChange: (updates: Partial<FlightFormState>) => void;
  airports: Airport[];
  dictionary: SystemDictionary;
  title?: string;
  errorPrefix?: string;
  onRemove?: () => void;
}

export function FlightInfoForm({
  state,
  errors,
  onChange,
  airports,
  dictionary,
  title = "1. Информация о рейсе",
  errorPrefix = "",
  onRemove,
}: FlightInfoFormProps) {
  const errorKey = (field: string) => errors[`${errorPrefix}${field}`];

  const handleAirportChange = (field: "from" | "to") => (iata: string) => {
    if (!iata) {
      onChange({ [field]: null });
      return;
    }
    const airport = airports.find((a) => a.IATA === iata);
    onChange({
      [field]: airport
        ? {
            city: airport.City.ru,
            airport: airport.Name.ru,
            airportCode: airport.IATA,
            country: airport.Country.ru,
          }
        : null,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{title}</h2>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Откуда</Label>
          <DynamicSelectFilter
            placeholder="Откуда"
            data={airports}
            value={state.from?.airportCode ?? ""}
            onChange={handleAirportChange("from")}
            valueExtractor={(a) => a.IATA}
            labelExtractor={(a) => `${a.IATA} — ${a.City.ru}`}
            className="w-full"
          />
          {errorKey("from") && <p className="text-xs text-destructive">{errorKey("from")}</p>}
        </div>

        <div className="space-y-1">
          <Label>Куда</Label>
          <DynamicSelectFilter
            placeholder="Куда"
            data={airports}
            value={state.to?.airportCode ?? ""}
            onChange={handleAirportChange("to")}
            valueExtractor={(a) => a.IATA}
            labelExtractor={(a) => `${a.IATA} — ${a.City.ru}`}
            className="w-full"
          />
          {errorKey("to") && <p className="text-xs text-destructive">{errorKey("to")}</p>}
        </div>

        <div className="space-y-1">
          <Label>Дата вылета</Label>
          <DatePickerInput
            value={state.departureDate}
            onSelect={(date) => onChange({ departureDate: date })}
            disabled={(date) => date < new Date()}
          />
          {errorKey("departureDate") && (
            <p className="text-xs text-destructive">{errorKey("departureDate")}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Дата прилёта</Label>
          <DatePickerInput
            value={state.arrivalDate}
            onSelect={(date) => onChange({ arrivalDate: date })}
            disabled={
              state.departureDate ? (date) => date < state.departureDate! : undefined
            }
          />
          {errorKey("arrivalDate") && (
            <p className="text-xs text-destructive">{errorKey("arrivalDate")}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Номер рейса</Label>
          <Input
            value={state.flightNumber}
            onChange={(e) => onChange({ flightNumber: e.target.value })}
            placeholder="KC123"
          />
          {errorKey("flightNumber") && (
            <p className="text-xs text-destructive">{errorKey("flightNumber")}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Авиакомпания</Label>
          <DynamicSelectFilter
            placeholder="Авиакомпания"
            data={dictionary.airlines}
            value={state.airline}
            onChange={(v) => onChange({ airline: v })}
            valueExtractor={(item) => item}
            labelExtractor={(item) => item}
            className="w-full"
          />
          {errorKey("airline") && (
            <p className="text-xs text-destructive">{errorKey("airline")}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Время вылета</Label>
          <div className="relative">
            <input
              type="time"
              value={state.departureTime}
              onChange={(e) => onChange({ departureTime: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 pr-10"
            />
            <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>
          {errorKey("departureTime") && (
            <p className="text-xs text-destructive">{errorKey("departureTime")}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Время прилёта</Label>
          <div className="relative">
            <input
              type="time"
              value={state.arrivalTime}
              onChange={(e) => onChange({ arrivalTime: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 pr-10"
            />
            <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>
          {errorKey("arrivalTime") && (
            <p className="text-xs text-destructive">{errorKey("arrivalTime")}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Багаж (кг)</Label>
          <Input
            type="number"
            min={0}
            value={state.baggageCheckedKg}
            onChange={(e) => onChange({ baggageCheckedKg: e.target.value })}
            placeholder="Например: 20"
          />
          {errorKey("baggageCheckedKg") && (
            <p className="text-xs text-destructive">{errorKey("baggageCheckedKg")}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Ручная кладь (кг)</Label>
          <Input
            type="number"
            min={0}
            value={state.baggageCarryOnKg}
            onChange={(e) => onChange({ baggageCarryOnKg: e.target.value })}
            placeholder="Например: 7"
          />
          {errorKey("baggageCarryOnKg") && (
            <p className="text-xs text-destructive">{errorKey("baggageCarryOnKg")}</p>
          )}
        </div>

        <div className="space-y-1 col-span-2">
          <Label>Цена (₸)</Label>
          <Input
            type="number"
            min={0}
            value={state.price}
            onChange={(e) => onChange({ price: e.target.value })}
            placeholder="0"
          />
          {errorKey("price") && <p className="text-xs text-destructive">{errorKey("price")}</p>}
        </div>
      </div>
    </div>
  );
}
